export type AttachmentSlot = 'front' | 'back' | 'note';

export interface LocalEncryptedAttachment {
  attachmentId: string;
  recordId: string;
  slot: AttachmentSlot;
  cryptoVersion: 1;
  wrappedAttachmentKey: string;
  keyNonce: string;
  originalNonce: string;
  originalChecksum: string;
  originalCiphertext?: ArrayBuffer;
  thumbnailNonce?: string;
  thumbnailChecksum?: string;
  thumbnailCiphertext?: ArrayBuffer;
  mimeType: string;
  sizeBytes: number;
  thumbnailSizeBytes?: number;
  state: 'local' | 'pending' | 'uploaded' | 'failed';
  lastError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AttachmentDeletionRequest {
  attachmentId: string;
  recordId: string;
  sourceRevisionId: string;
  queuedAt: string;
  updatedAt: string;
  attempts: number;
  lastError?: string;
}

const DATABASE_NAME = 'safe-wallet-attachments-v1';
const ATTACHMENT_STORE_NAME = 'encrypted-attachments';
const DELETION_STORE_NAME = 'attachment-deletions';
const DATABASE_VERSION = 2;
let databasePromise: Promise<IDBDatabase> | null = null;

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Local attachment storage failed.'));
  });
}

function openAttachmentDatabase(): Promise<IDBDatabase> {
  if (!('indexedDB' in globalThis)) {
    return Promise.reject(new Error('This browser does not support durable local attachment storage.'));
  }
  databasePromise ??= new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(ATTACHMENT_STORE_NAME)) {
        const store = database.createObjectStore(ATTACHMENT_STORE_NAME, { keyPath: 'attachmentId' });
        store.createIndex('by-record', 'recordId', { unique: false });
        store.createIndex('by-state', 'state', { unique: false });
      } else {
        const store = request.transaction?.objectStore(ATTACHMENT_STORE_NAME);
        if (store && !store.indexNames.contains('by-state')) {
          store.createIndex('by-state', 'state', { unique: false });
        }
      }
      if (!database.objectStoreNames.contains(DELETION_STORE_NAME)) {
        database.createObjectStore(DELETION_STORE_NAME, { keyPath: 'attachmentId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open local attachment storage.'));
    request.onblocked = () => reject(new Error('Local attachment storage is blocked by another Safe Wallet tab.'));
  });
  return databasePromise;
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Local attachment transaction failed.'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Local attachment transaction was cancelled.'));
  });
}

export async function saveLocalAttachment(value: LocalEncryptedAttachment): Promise<void> {
  const database = await openAttachmentDatabase();
  const transaction = database.transaction(ATTACHMENT_STORE_NAME, 'readwrite');
  transaction.objectStore(ATTACHMENT_STORE_NAME).put(value);
  await transactionComplete(transaction);
}

export async function loadLocalAttachment(attachmentId: string): Promise<LocalEncryptedAttachment | null> {
  const database = await openAttachmentDatabase();
  const transaction = database.transaction(ATTACHMENT_STORE_NAME, 'readonly');
  const result = await requestResult(transaction.objectStore(ATTACHMENT_STORE_NAME).get(attachmentId));
  await transactionComplete(transaction);
  return (result as LocalEncryptedAttachment | undefined) ?? null;
}

export async function listLocalAttachments(): Promise<LocalEncryptedAttachment[]> {
  const database = await openAttachmentDatabase();
  const transaction = database.transaction(ATTACHMENT_STORE_NAME, 'readonly');
  const result = await requestResult(transaction.objectStore(ATTACHMENT_STORE_NAME).getAll());
  await transactionComplete(transaction);
  return result as LocalEncryptedAttachment[];
}

export async function updateLocalAttachment(
  attachmentId: string,
  changes: Partial<Omit<LocalEncryptedAttachment, 'attachmentId' | 'recordId' | 'createdAt'>>,
): Promise<LocalEncryptedAttachment | null> {
  const existing = await loadLocalAttachment(attachmentId);
  if (!existing) return null;
  const updated: LocalEncryptedAttachment = {
    ...existing,
    ...changes,
    attachmentId: existing.attachmentId,
    recordId: existing.recordId,
    createdAt: existing.createdAt,
    updatedAt: new Date().toISOString(),
  };
  await saveLocalAttachment(updated);
  return updated;
}

export async function deleteLocalAttachment(attachmentId: string): Promise<void> {
  const database = await openAttachmentDatabase();
  const transaction = database.transaction(ATTACHMENT_STORE_NAME, 'readwrite');
  transaction.objectStore(ATTACHMENT_STORE_NAME).delete(attachmentId);
  await transactionComplete(transaction);
}

export async function deleteLocalAttachments(attachmentIds: Iterable<string>): Promise<void> {
  const ids = [...attachmentIds];
  if (ids.length === 0) return;
  const database = await openAttachmentDatabase();
  const transaction = database.transaction(ATTACHMENT_STORE_NAME, 'readwrite');
  const store = transaction.objectStore(ATTACHMENT_STORE_NAME);
  ids.forEach((id) => store.delete(id));
  await transactionComplete(transaction);
}

export async function queueAttachmentDeletion(
  attachmentId: string,
  recordId: string,
  sourceRevisionId: string,
): Promise<void> {
  const database = await openAttachmentDatabase();
  const transaction = database.transaction(DELETION_STORE_NAME, 'readwrite');
  const store = transaction.objectStore(DELETION_STORE_NAME);
  const existing = await requestResult(store.get(attachmentId)) as AttachmentDeletionRequest | undefined;
  const timestamp = new Date().toISOString();
  store.put({
    ...existing,
    attachmentId,
    recordId,
    sourceRevisionId,
    queuedAt: existing?.queuedAt ?? timestamp,
    updatedAt: timestamp,
    attempts: 0,
    lastError: undefined,
  } satisfies AttachmentDeletionRequest);
  await transactionComplete(transaction);
}

export async function listAttachmentDeletions(): Promise<AttachmentDeletionRequest[]> {
  const database = await openAttachmentDatabase();
  const transaction = database.transaction(DELETION_STORE_NAME, 'readonly');
  const result = await requestResult(transaction.objectStore(DELETION_STORE_NAME).getAll());
  await transactionComplete(transaction);
  return result as AttachmentDeletionRequest[];
}

export async function updateAttachmentDeletion(
  attachmentId: string,
  changes: Partial<Omit<AttachmentDeletionRequest, 'attachmentId' | 'recordId' | 'sourceRevisionId' | 'queuedAt'>>,
): Promise<void> {
  const database = await openAttachmentDatabase();
  const transaction = database.transaction(DELETION_STORE_NAME, 'readwrite');
  const store = transaction.objectStore(DELETION_STORE_NAME);
  const existing = await requestResult(store.get(attachmentId)) as AttachmentDeletionRequest | undefined;
  if (existing) {
    store.put({
      ...existing,
      ...changes,
      attachmentId: existing.attachmentId,
      recordId: existing.recordId,
      sourceRevisionId: existing.sourceRevisionId,
      queuedAt: existing.queuedAt,
      updatedAt: new Date().toISOString(),
    } satisfies AttachmentDeletionRequest);
  }
  await transactionComplete(transaction);
}

export async function deleteAttachmentDeletion(attachmentId: string): Promise<void> {
  const database = await openAttachmentDatabase();
  const transaction = database.transaction(DELETION_STORE_NAME, 'readwrite');
  transaction.objectStore(DELETION_STORE_NAME).delete(attachmentId);
  await transactionComplete(transaction);
}
